import {
  applyRefractionActions,
  Intensity,
  LensCustomization,
  Order,
  OrderConfig,
} from '@ca/common'
import { getMultiOrderValues, RegionConfig } from '@ca/common/types'
import { useResolvedPd } from '@order/PdSection'
import FrameUtils from '@frame/FrameUtils'
import { StockUtils } from '@frame/StockUtils'
import { OrderItem, StepMode, StepsState } from '@order/orderTypes'
import { VariableValues } from '@order/utils/OrderVariables'
import { getPackageVariant } from '@order/utils/packages/PackagesUtils'
import { getPrismPd } from '@screens/vision/prescription/form/calculateBase'
import { captureException } from '@sentry/react-native'
import { useConfirmCustomer } from '@user/customer/confirmation/useConfirmCustomer'
import { useIsFocused } from '@ui/hooks/useIsFocused'
import { useRemoteConfig } from 'hooks/useRemoteConfigStore'
import useSession, { useCustomer, useStaff, useStore } from 'hooks/useSession'
import { useMemo } from 'react'
import { OrderState } from './state/orderState'
import { useSavedOrder } from './useCartState'
import { resolveDealById, useDealByBundleType } from './useDeal'
import { useOrder, useOrderState } from './useOrderState'
import { usePrescription } from './usePrescription'
import useStepsState from './useStepsState'
import {
  getOrderValues,
  getValuesDefaults,
  withPackageVariantValues,
} from './values/OrderValues'

const PayloadErrors = {
  trace_not_available: 'trace_not_available',
  invalid_stock_type: 'invalid_stock_type',
  missing_lens: 'missing_lens',
  missing_frame: 'missing_frame',
  missing_refraction: 'missing_refraction',
  missing_strategy_ref: 'missing_strategy_ref',
  missing_lens_prefix: 'missing_lens_prefix',
  unknown_error: 'unknown_error',
  no_orders: 'no_orders',
}

const getIndexSuffix = (
  indexSuffix?: string | number,
  tinting?: string | number
) => {
  if (indexSuffix) return indexSuffix.toString()
  if (tinting === 'photolite') return 'F'
  if (tinting === 'polarlite') return 'N'
  return ''
}

const getLens = ({
  lensPrefix,
  index,
  coating,
  tinting,
  indexSuffix,
}: {
  lensPrefix: string | number
  index?: string | number
  coating?: string | number
  tinting?: string | number
  indexSuffix?: string | number
}) => {
  if (!lensPrefix || !index || !coating) {
    console.warn('Invalid lens data', lensPrefix, index, coating)
    return undefined
  }

  const suffix = getIndexSuffix(indexSuffix, tinting)

  return `${lensPrefix}-${index}${suffix}-${coating}`.toUpperCase()
}

const getTinting = (input: {
  tinting?: string | number
  intensity?: string | number
  color?: string | number
  colorPrefix?: string | number
}) => {
  // We use color for photolite and polarlite
  if (
    input.tinting === 'photolite' ||
    input.tinting === 'polarlite' ||
    input.tinting === 'none' ||
    input.tinting === 'na'
  ) {
    return ''
  }
  if (!input.intensity) {
    console.warn('missing_intensity', input)
    return ''
  }
  if (!input.color) {
    console.warn('missing_color', input)

    return ''
  }

  if (input.intensity === 'full') {
    return `${input.colorPrefix || ''}${input.color || ''}`
  }

  if (input.intensity === '75-35') {
    return `${input.colorPrefix || ''}${input.color}G7530`
  }
  const gradientCode = Intensity.utils.getGradientCode(input.intensity)
  if (gradientCode) {
    return `${input.colorPrefix || ''}${input.color}${gradientCode}`
  }

  return `${input.colorPrefix || ''}${input.color}${input.intensity}`
}

const getColor = (input: {
  tinting?: string
  color?: string
  colorPrefix?: string
  intensity?: string
}) => {
  if (
    input.tinting === 'na' ||
    input.tinting === 'none' ||
    input.tinting === 'filter' ||
    input.tinting === 'tinting'
  ) {
    return ''
  }

  if (!input.color) {
    console.warn('missing_color', input)
    return ''
  }
  return `${input.colorPrefix || ''}${input.color}`
}
const getStringValue = (key: string, input: VariableValues) => {
  if (!input[key]) {
    return ''
  }
  return input[key].toString()
}

const getBooleanValue = (key: string, input: VariableValues) => {
  if (!input[key]) {
    return false
  }
  return input[key] === 'true'
}

const getFrameStock = (
  item: OrderItem,
  stock: OrderState['stock'],
  config: RegionConfig
) => {
  const frameStock = stock[StockUtils.getItemKey(item.id, 'frame')]
  // If we have no stock theres no local option
  if (
    !frameStock &&
    item.frame &&
    StockUtils.sources.global(item.frame, config)
  ) {
    return false
  }

  if (frameStock?.source === 'local') {
    return true
  }
  if (frameStock?.source === 'global') {
    return false
  }

  throw new Error(PayloadErrors.invalid_stock_type)
}

const getInventoryNo = (item: OrderItem, stock: OrderState['stock']) => {
  const frameStock = stock[StockUtils.getItemKey(item.id, 'frame')]
  if (!frameStock) {
    return undefined
  }
  if (frameStock.source !== 'local') {
    return undefined
  }

  if (!frameStock.inventory) {
    return undefined
  }
  if (item.frame?.default_code === frameStock.inventory) {
    return undefined
  }
  if (!frameStock.trace) {
    throw new Error(PayloadErrors.trace_not_available)
  }

  return frameStock.inventory
}

const getRefractionCustomizations = (input?: LensCustomization) => {
  if (!input) {
    return {}
  }
  const { corridor, ...rest } = input
  return rest
}

const resolveSteps = (states: Partial<Record<StepMode, StepsState>>) => {
  return Object.values(states).flat()
}

const addMultiOrderValues = (orders: Order[]) => {
  const items = orders.map((order) => {
    return {
      ...order,
      values: {
        values: order.variable_values || {},
        refraction: order.refraction,
      },
    }
  })
  const multiOrderValues = getMultiOrderValues(items)

  return orders.map((order, index) => {
    return {
      ...order,
      variable_values: multiOrderValues[index]?.values.values,
    }
  }) satisfies Order[]
}

const useOrderPayload = () => {
  const order = useOrder()
  const customer = useCustomer()
  const staff = useStaff()
  const store = useStore()
  const config = useRemoteConfig()
  const deals = useDealByBundleType()
  const focused = useIsFocused()
  const invalidateResult = useOrderState((s) => s.invalidateResult)
  const pd = useResolvedPd()
  const prescription = usePrescription()
  const result = useOrderState((s) => s.result)
  const currentState = useStepsState()
  const pendingOrder = useSession((s) => s.pendingOrder)
  const { isInvoiceRequired, invoice } = useConfirmCustomer()
  const savedOrder = useSavedOrder(pendingOrder?.savedOrderId)

  // If invoice is required or enabled try to use the invoice id from the customer
  const shouldUseInvoice = isInvoiceRequired || order.invoice
  const invoiceId = shouldUseInvoice ? invoice?.data?.id : null

  const steps = useOrderState((s) => s.steps)

  return useMemo(() => {
    if (
      !focused ||
      !customer ||
      !deals?.base ||
      !staff ||
      !prescription ||
      !config
    ) {
      const guardMissing = [
        !focused && 'focused',
        !customer && 'customer',
        !deals?.base && 'deals.base',
        !staff && 'staff',
        !prescription && 'prescription',
        !config && 'config',
      ].filter(Boolean) as string[]
      // Should not be handled
      return {
        payload: null,
        error: null,
        guardMissing,
      }
    }

    let orders: Order[] = []
    try {
      orders = order.items.map((item) => {
        const bundleSteps = order.bundles[item.bundleType]?.steps || []
        // If we have steps in the current state, use them
        const hasCurrentState =
          currentState.orderItemId === item.id &&
          Object.keys(currentState.states).length > 0
        const itemSteps = resolveSteps(
          hasCurrentState ? currentState.states : steps[item.id]
        )
        const fullState = [...bundleSteps, ...itemSteps]
        const refraction = applyRefractionActions(prescription, fullState, pd)
        const deal = deals[item.bundleType]
        const defaults = getValuesDefaults({
          dealCode: deal?.code,
          frames: deal?.frames,
          dealType: deal?.type,
          customer,
          store,
        })
        const initialValues = getOrderValues({
          order,
          frame: item.frame,
          fullState,
          defaults,
          bundle: item.bundleType,
        })

        const selectedPackage = order?.packageCfg[item.bundleType]
        const packageVariant =
          selectedPackage &&
          getPackageVariant(config.packages[selectedPackage.id], config, {
            values: initialValues,
            refraction,
          })

        const { values } = withPackageVariantValues({
          values: {
            values: initialValues,
            refraction,
          },
          fullState,
          variant: packageVariant,
        })

        const lensPrefix =
          values.lensPrefix ||
          packageVariant?.lensPrefix ||
          selectedPackage?.lensPrefix

        if (!lensPrefix) {
          throw new Error(PayloadErrors.missing_lens_prefix)
        }

        const lens = getLens({
          ...values,
          lensPrefix,
        })

        const frame = item.frame?.default_code
        if (!lens) {
          throw new Error(PayloadErrors.missing_lens)
        }
        if (!frame) {
          throw new Error(PayloadErrors.missing_frame)
        }
        if (!refraction) {
          throw new Error(PayloadErrors.missing_refraction)
        }

        const shape = {
          x_ext: item.frame?.shape?.extend_x || 0,
          y_ext: item.frame?.shape?.extend_y || 0,
        }

        const strategy_ref = deals[item.bundleType]?.code?.toUpperCase()
        if (!strategy_ref) {
          throw new Error(PayloadErrors.missing_strategy_ref)
        }

        const getExtraFirstId = () => {
          const baseDeal = deals['base']
          const extraDeal = baseDeal?.extra
            ? resolveDealById(baseDeal.extra)
            : undefined

          if (!extraDeal?.addFirstId) {
            return undefined
          }

          const firstOrderId = savedOrder?.result?.orders[0]?.id
          if (firstOrderId) {
            return firstOrderId
          }

          const firstItem = order.items.find(
            (item) => item.bundleType === 'base'
          )

          if (!firstItem) {
            return undefined
          }

          const firstItemOrder = result?.orders.find(
            (orderRes) => orderRes.data.localId === firstItem?.id
          )

          return firstItemOrder?.id ?? undefined
        }

        // tgbo submits the second pair as its own base order, so the link comes
        // from the pending deal rather than an extra bundle
        const getPendingFirstId = () => {
          const firstOrderId = savedOrder?.result?.orders[0]?.id
          if (!firstOrderId) {
            return undefined
          }

          const pendingDeal = resolveDealById(pendingOrder?.deal.dealId)

          return pendingDeal?.addFirstId ? firstOrderId : undefined
        }

        const getFirstId = () => {
          if (item.bundleType === 'extra') {
            return getExtraFirstId()
          }
          return getPendingFirstId()
        }
        const customizations = getRefractionCustomizations(item.customization)
        const orderRefraction = { ...refraction, ...shape }
        const refractionWithPrismPd = {
          ...orderRefraction,
          ...customizations,
          ...getPrismPd(orderRefraction),
        }
        return {
          localId: item.id,
          frame,
          // Its a bit weird that we use temple name here
          // For now the name contains the code
          leg: item.temple?.name,
          frame_from_stock: getFrameStock(item, order.stock, config.region),
          // TODO: Get temple inventory no and stock?
          inventory_no: getInventoryNo(item, order.stock),
          ...FrameUtils.getHeights(item.frame),
          lens,
          note: order.comment || '',
          premium_insurance: getBooleanValue('insurance', values),
          refraction: refractionWithPrismPd,
          mirror: getStringValue('mirror', values),
          tinting: getTinting(values),
          color: getColor(values),
          lifestyle: getStringValue('lifestyle', values),
          distance: getStringValue('distance', values),
          corridor: item.customization?.corridor?.toString(),
          strategy_ref,
          first_id: getFirstId(),
          variable_values: values,
        } satisfies Order
      })
    } catch (e) {
      console.error(e)
      // Clear result so we don't allow to continue
      invalidateResult()
      if (e instanceof Error) {
        captureException(e, {
          level: 'warning',
          tags: {
            location: 'useOrderPayload',
          },
          extra: {
            order: order,
            prescription,
            pd,
            deals,
          },
        })
        return {
          payload: null,
          error: e.message,
        }
      }
      captureException(new Error('Payload: unknown error'), {
        tags: {
          location: 'useOrderPayload',
        },
        extra: {
          order: order,
          prescription,
          pd,
          deals,
        },
      })
      return {
        payload: null,
        error: PayloadErrors.unknown_error,
      }
    }

    if (!orders.length) {
      console.warn('No orders generated')
      return {
        payload: null,
        error: PayloadErrors.no_orders,
      }
    }

    const res = {
      orders: addMultiOrderValues(orders),
      // Customer invoice id // fallback to customer id
      customer_id: customer.id,
      store_id: staff.store_id,
      staff_id: staff.id,
      invoice_id: invoiceId,
    } satisfies OrderConfig

    return {
      payload: res,
      error: null,
    }
  }, [
    order,
    customer,
    result?.orders,
    config,
    invalidateResult,
    staff,
    deals,
    prescription,
    focused,
    currentState.orderItemId,
    currentState.states,
    invoiceId,
    pd,
    store,
    steps,
    savedOrder?.result?.orders,
    pendingOrder?.deal,
  ])
}

export default useOrderPayload
